import { InputBaseComponentProps } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import { useEffect, useState } from 'react';

import {
  createNewSurveyAnswer,
  deleteSurveyAnswer,
} from 'redux/slices/legacySurveySlice.slice';
import { useAppDispatch } from 'redux/storeHooks';
import type { SurveyProps } from './types';

const TextInput = ({
  question,
  register,
  unregister,
  error,
  response,
  setValue,
  getValues,
}: SurveyProps) => {
  const [inputValue, setInputValue] = useState('');
  const [savedValue, setSavedValue] = useState('');
  const dispatch = useAppDispatch();

  const validateMin = value => {
    if (question.inputType !== 'number') return true;

    let v = value;
    if (isNaN(value)) {
      v = 0;
    }

    if (typeof question.input.min === 'number') {
      return parseInt(v) >= question.input.min;
    }
    return true;
  };

  const validateMax = value => {
    if (question.inputType !== 'number') return true;

    let v = value;
    if (isNaN(value)) {
      v = 0;
    }

    if (typeof question.input.max === 'number') {
      return parseInt(v) <= question.input.max;
    }
    return true;
  };

  const registerProps = register(question.id, {
    required: question.isRequired,
    valueAsNumber: question.inputType === 'number',
    validate: {
      min: v => validateMin(v),
      max: v => validateMax(v),
    },
    onChange: e => setInputValue(e.target.value),
  });
  useEffect(() => {
    const initialValue = getValues(question.id);
    let value;
    if (initialValue) {
      value = initialValue;
    } else {
      if (question.input.inputType === 'number') {
        value = question.answer?.number;
      } else {
        value = question.answer?.text;
      }
    }

    // if is number and 0, we don't want to set to empty string
    // else, if null or undefined, we want empty string.
    if (typeof value !== 'number') {
      value = value || '';
    }

    if (typeof value === 'number') {
      if (isNaN(value)) {
        value = '';
      }
    }

    setSavedValue(value);
    setInputValue(value);

    return () => {
      unregister(question.id);
    };
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (savedValue !== inputValue && !error) {
        submit();
      }
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [inputValue]);

  const submit = () => {
    const saveVar = question.input.inputType == 'number' ? 'number' : 'text';

    setValue(question.id, inputValue);
    setSavedValue(inputValue);

    if (inputValue) {
      dispatch(
        createNewSurveyAnswer({
          body: {
            questionId: question.id,
            responseId: response.id,
            [saveVar]: inputValue,
          },
        })
      );
    } else {
      dispatch(
        deleteSurveyAnswer({
          params: {
            questionId: question.id,
            responseId: response.id,
          },
        })
      );
    }
  };

  const helperText = () => {
    if (!error) return '';

    if (error?.type === 'min') {
      return `Value must be at ${question.input.min} or more.`;
    }
    if (error?.type === 'max') {
      return `Value must be ${question.input.max} or less.`;
    }
    if (error?.type === 'required') {
      return 'A value is required';
    }

    return 'An error has occurred';
  };

  const inputProps: InputBaseComponentProps = {
    type: question.inputType === 'number' ? 'number' : null,
    min: question.input.min,
    max: question.input.max,
    required: question.isRequired,
  };

  if (question.inputType == 'textarea') {
    inputProps['minRows'] = 3;
  }

  return (
    <FormControl
      fullWidth
      required={question.isRequired}
      sx={{
        marginTop: '12px',
      }}
    >
      <TextField
        {...registerProps}
        inputProps={{ ...inputProps }}
        multiline={question.inputType === 'text'}
        error={!!error}
        helperText={helperText()}
        id={question.id}
        label={question.inputTitle || ''}
        placeholder={question.placeholder}
        required={question.isRequired}
        variant="outlined"
        value={inputValue}
      ></TextField>
    </FormControl>
  );
};

export default TextInput;
